import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Box,
  Pagination,
  TextFilter,
  CollectionPreferences,
  SpaceBetween,
  Button,
  DateRangePicker,
  FormField,
} from '@amzn/awsui-components-react/polaris';
import { apiCall } from '../../../utils';
import PropTypes from 'prop-types';
import {getDeploymentColumns, VISIBLE_CONTENT_OPTIONS} from './columns';
import { i18nStrings } from '../../../../Domain/KeyEvents/utils';

export const DeploymentDetailsTable = ({stackName, releaseVersion, status}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preferences, setPreferences] = useState({
    pageSize: 20,
    visibleContent: ['clusterId', 'status', 'requestSource', 'deploymentId', 'creationTime', 'lastModificationTime', 'deploymentType', 'notificationDetails', 'stackName', 'releaseVersion']
  });
  const [filteringText, setFilteringText] = useState('');
  const [dateRange, setDateRange] = useState(undefined);
  const tableRef = useRef(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [nextToken, setNextToken] = useState(null);
  const [allData, setAllData] = useState([]);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  const columnDefinitions = getDeploymentColumns();

  const fetchStatusDetails = async (token = null) => {
    setIsPaginationLoading(true);
    setIsLoading(true);
    try {
      const payload = {
        stackName,
        releaseVersion,
        status,
        maxResults: 50
      };

      if (token) {
        payload.nextToken = token;
      }

      if (dateRange) {
        if (dateRange.type === 'absolute') {
          payload.updateStartTime = Math.floor(new Date(dateRange.startDate).getTime() / 1000);
          payload.updateEndTime = Math.floor(new Date(dateRange.endDate).getTime() / 1000);
        } else if (dateRange.type === 'relative') {
          const now = new Date();
          const startTime = new Date();

          switch (dateRange.unit) {
            case 'minute':
              startTime.setMinutes(startTime.getMinutes() - dateRange.amount);
              break;
            case 'hour':
              startTime.setHours(startTime.getHours() - dateRange.amount);
              break;
            default:
              break;
          }

          payload.updateStartTime = Math.floor(startTime.getTime() / 1000);
          payload.updateEndTime = Math.floor(now.getTime() / 1000);
        }
      }

      const response = await apiCall('/external/listreleaseVersion', payload);

      if (response.statusCode === 200) {
        const newData = response.body.customerDeploymentConfigsList || [];
        setAllData(prevData => {
          const currentData = Array.isArray(prevData) ? prevData : [];
          return [...currentData, ...newData];
        });
        setNextToken(response.body.nextToken || null);
        setError(null);
      } else {
        setError('Failed to fetch deployment details');
      }
    } catch (error) {
      console.error('Error fetching deployment details:', error);
      setError('Failed to fetch deployment details');
    } finally {
      setIsLoading(false);
      setIsPaginationLoading(false);
    }
  };

  const resetAndFetch = (newDateRange = null) => {
    setDateRange(newDateRange);
    setCurrentPageIndex(1);
    setAllData([]);
    fetchStatusDetails(null);
  };

  useEffect(() => {
    if (status) {
      resetAndFetch(dateRange);
    }
  }, [status]);

  const getCurrentPageItems = () => {
    if (isPaginationLoading) {
      const start = (currentPageIndex - 1) * preferences.pageSize;
      const end = start + preferences.pageSize;
      return allData.slice(start - preferences.pageSize, end - preferences.pageSize);
    }

    const filteredItems = getFilteredItems();
    const start = (currentPageIndex - 1) * preferences.pageSize;
    const end = start + preferences.pageSize;
    return filteredItems.slice(start, end);
  };

  const handlePageChange = async ({ detail }) => {
    if (isPaginationLoading) return;

    const newPageIndex = detail.currentPageIndex;
    setCurrentPageIndex(newPageIndex);

    const totalItemsFetched = allData.length;
    const requiredItems = newPageIndex * preferences.pageSize;

    if (requiredItems > totalItemsFetched && nextToken) {
      await fetchStatusDetails(nextToken);
    }
  };

  const getFilteredItems = () => {
    if (!filteringText) return allData;

    const searchText = filteringText.toLowerCase();
    return allData.filter(item =>
      Object.values(item).some(value =>
        String(value).toLowerCase().includes(searchText)
      )
    );
  };

  const getDateRangeText = () => {
    if (!dateRange) return '';
    if (dateRange.type === 'absolute') {
      return `${new Date(dateRange.startDate).toLocaleDateString()} - ${new Date(dateRange.endDate).toLocaleDateString()}`;
    }
    return `Last ${dateRange.amount} ${dateRange.unit}${dateRange.amount > 1 ? 's' : ''}`;
  };

  if (error) {
    return <Box textAlign="center" color="error">{error}</Box>;
  }

  return (
    <div ref={tableRef}>
      <Table
        columnDefinitions={columnDefinitions}
        items={getCurrentPageItems()}
        loading={isLoading || isPaginationLoading}
        loadingText="Loading clusters..."
        header={
          <SpaceBetween size="m">
            <div>{`Clusters in ${status} status (${allData.length})`}</div>
            <SpaceBetween size="m" direction="horizontal" alignItems="center">
              <FormField stretch={true}>
                <DateRangePicker
                  onChange={({ detail }) => resetAndFetch(detail.value)}
                  value={dateRange}
                  showClearButton
                  relativeOptions={[
                    {
                      key: "previous-5-minutes",
                      amount: 5,
                      unit: "minute",
                      type: "relative"
                    },
                    {
                      key: "previous-30-minutes",
                      amount: 30,
                      unit: "minute",
                      type: "relative"
                    },
                    {
                      key: "previous-1-hour",
                      amount: 1,
                      unit: "hour",
                      type: "relative"
                    },
                    {
                      key: "previous-6-hours",
                      amount: 6,
                      unit: "hour",
                      type: "relative"
                    }
                  ]}
                  isValidRange={range => {
                    if (range?.type === "absolute") {
                      const [startDateWithoutTime] = range.startDate.split("T");
                      const [endDateWithoutTime] = range.endDate.split("T");

                      if (!startDateWithoutTime || !endDateWithoutTime) {
                        return {
                          valid: false,
                          errorMessage: "The selected date range is incomplete. Select a start and end date for the date range."
                        };
                      }

                      if (new Date(range.startDate) - new Date(range.endDate) > 0) {
                        return {
                          valid: false,
                          errorMessage: "The selected date range is invalid. The start date must be before the end date."
                        };
                      }
                    }
                    return { valid: true };
                  }}
                  i18nStrings={i18nStrings}
                  placeholder="Filter by a date and time range"
                />
              </FormField>
              {dateRange && (
                <Button onClick={() => resetAndFetch(null)}>
                  Reset Filter
                </Button>
              )}
            </SpaceBetween>
            {dateRange && (
              <Box color="text-status-info" fontSize="body-s">
                Filtered by date range: {getDateRangeText()}
              </Box>
            )}
          </SpaceBetween>
        }
        filter={
          <TextFilter
            filteringPlaceholder="Find clusters"
            filteringText={filteringText}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPageIndex(1);
            }}
            countText={`${getFilteredItems().length} matches`}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            pageSizePreference={{
              title: "Page size",
              options: [
                { value: 10, label: "10 clusters" },
                { value: 20, label: "20 clusters" },
                { value: 50, label: "50 clusters" }
              ]
            }}
            visibleContentPreference={{
              title: 'Column preferences',
              options: [
                {
                  label: 'Main attributes',
                  options: VISIBLE_CONTENT_OPTIONS
                }
              ]
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
          />
        }
        visibleColumns={preferences.visibleContent}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No data available</b>
            <Box variant="p" color="inherit">
              No clusters found in {status} status
            </Box>
          </Box>
        }
        variant="embedded"
        stickyHeader
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            onChange={handlePageChange}
            pagesCount={Math.ceil(getFilteredItems().length / preferences.pageSize) + (nextToken ? 1 : 0)}
            pageSize={preferences.pageSize}
            disabled={isPaginationLoading}
          />
        }
      />
    </div>
  );
};

DeploymentDetailsTable.propTypes = {
  stackName: PropTypes.string.isRequired,
  releaseVersion: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  onClose: PropTypes.func
};